import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInputProps, SelectOption } from '@microservices/wiskey-react-components'

import {
  ACTION_CODE,
  ENTITY_TYPES_REQUEST,
  EVENT_CODE,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
  REGEX,
} from '@constants'
import { AutocompleteOption, FormCommand, ModalType } from '@types'

export type ActionForm = {
  id?: number | string
  code: string
  title: string
  actionCode?: ACTION_CODE
  actionTitle?: string
  eventCode?: EVENT_CODE | null
  eventTitle?: string
  params?: string
  columnToSort?: AutocompleteOption | null
  order?: string | number
  json?: boolean
  objectFormCode?: AutocompleteOption | null
  targetColumns?: AutocompleteOption[] | null
  formObject: AutocompleteOption | null
  contextMenu?: AutocompleteOption<number> | null
  tooltip?: AutocompleteOption<number> | null
  command?: FormCommand[] | null
  jsValue?: string
  draggableElementText?: AutocompleteOption<string> | null
  variable?: AutocompleteOption | null
  redefinitionCommands?: FormCommand[] | null
  view?: AutocompleteOption | null
}

type useInputsParams = {
  setValue: UseFormSetValue<ActionForm>
  actionTypeOptions: SelectOption[]
  contextMenusOptions: AutocompleteOption[]
  tooltipOptions: AutocompleteOption[]
  targetColumnsOptions: AutocompleteOption[]
  eventOptions: SelectOption[]
  objectOptions: AutocompleteOption[]
  watchJson?: boolean
  watchEvent?: EVENT_CODE
  watchActionCode?: ACTION_CODE
  watchFormObject?: AutocompleteOption | null
  formCodeOptions: AutocompleteOption[]
  viewOptions: AutocompleteOption[]
  loadingAllForm?: boolean
  loadingBindingValuesEvents?: boolean
  isAllObjectsLoading: boolean
  modalType: ModalType
  variablesOptions: AutocompleteOption[]
  draggableElementTextOptions: AutocompleteOption[]
  setIsOpenScriptValueDialog: (value: boolean) => void
  setCurrentNameScriptDialog: (value: string) => void
  loadingAllViews?: boolean
  type: ENTITY_TYPES_REQUEST
}

export const useInputs = ({
  setValue,
  actionTypeOptions,
  eventOptions,
  objectOptions,
  watchJson,
  watchEvent,
  watchActionCode,
  watchFormObject,
  formCodeOptions,
  loadingAllForm,
  targetColumnsOptions,
  contextMenusOptions,
  tooltipOptions,
  loadingBindingValuesEvents,
  isAllObjectsLoading,
  modalType,
  variablesOptions,
  draggableElementTextOptions,
  setIsOpenScriptValueDialog,
  setCurrentNameScriptDialog,
  viewOptions,
  loadingAllViews,
  type,
}: useInputsParams) => {
  const { t } = useTranslation()

  const isRightColumnClick = [
    EVENT_CODE.ON_COLUMN_RIGHT_CLICK,
    EVENT_CODE.ON_HEADER_TOOLTIP,
    EVENT_CODE.ON_CELL_TOOLTIP,
  ].includes(watchEvent as EVENT_CODE)

  const isContextMenu = watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU
  const isTooltip = watchActionCode === ACTION_CODE.OPEN_TOOLTIP
  const isEmptyActionCode = !watchActionCode
  const isShowJSValueField =
    watchActionCode === ACTION_CODE.MODIFY_DATA ||
    watchActionCode === ACTION_CODE.SET_VARIABLE_VALUE ||
    watchActionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE

  const targetInput = isRightColumnClick
    ? [
        {
          name: 'targetColumns',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: t('entityActionDialog.targetColumn.placeholder'),
          label: t('entityActionDialog.targetColumn.label'),
          autocompleteOptions: targetColumnsOptions,
          loading: loadingAllForm,
          rules: { required: true },
          multiple: true,
        },
      ]
    : []

  const contextMenuOrTooltipInput = isContextMenu
    ? [
        ...targetInput,
        {
          name: 'contextMenu',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: t('entityActionDialog.contextMenu.placeholder'),
          label: t('entityActionDialog.contextMenu.label'),
          autocompleteOptions: contextMenusOptions,
          loading: loadingAllForm,
          rules: { required: true },
        },
      ]
    : [
        ...targetInput,
        {
          name: 'tooltip',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: t('entityActionDialog.tooltip.placeholder'),
          label: t('entityActionDialog.tooltip.label'),
          autocompleteOptions: tooltipOptions,
          loading: loadingAllForm,
          rules: { required: true },
        },
      ]

  const actionValueInputs = [
    {
      name: 'formObject',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      label: t('actionInputs.formObject.label'),
      placeholder: t('actionInputs.formObject.placeholder'),
      autocompleteOptions: objectOptions,
      loading: isAllObjectsLoading,
      rules: {
        required: true,
        validate: (value: string) => value !== t('actionInputs.formObject.placeholder'),
      },
      onChangeAutocomplete: () => {
        setValue('objectFormCode', null)
      },
    },
    {
      name: 'objectFormCode',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: t('placeholder.objectFormCode'),
      label: t('label.objectFormCode'),
      autocompleteOptions: formCodeOptions,
      loading: loadingAllForm,
      rules: { required: true },
      labelSx: { ml: 1 },
      disabled: !watchFormObject,
    },
  ]

  const actionViewInputs = [
    {
      name: 'formObject',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      label: t('actionInputs.viewObject.label'),
      placeholder: t('actionInputs.viewObject.placeholder'),
      autocompleteOptions: objectOptions,
      loading: isAllObjectsLoading,
      rules: {
        required: true,
      },
      onChangeAutocomplete: () => {
        setValue('view', null)
      },
    },
    {
      name: 'view',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: t('actionInputs.view.placeholder'),
      label: t('actionInputs.view.label'),
      autocompleteOptions: viewOptions,
      loading: loadingAllViews,
      rules: { required: true },
      labelSx: { ml: 2, width: 60, minWidth: 60 },
      disabled: !watchFormObject,
    },
  ]

  const actionJSONValueInputs: FormInputProps[] = [
    {
      name: 'json',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: t('label.json'),
      labelPlacement: 'end',
    },
    ...(watchJson
      ? [
          {
            name: 'params',
            inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
            label: t('actionInputs.params.label'),
            placeholder: t('actionInputs.params.placeholder'),
            rules: { required: true, validate: (value: string) => value.trim().length !== 0 },
          },
        ]
      : []),
  ]

  const actionInputs: FormInputProps[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('actionInputs.code.placeholder'),
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      label: t('actionInputs.code.label'),
      disabled: modalType === 'edit',
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'title',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('actionInputs.title.placeholder'),
      label: t('actionInputs.title.label'),
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'actionCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: t('actionInputs.actionCode.label'),
      placeholder: t('placeholder.actionType'),
      selectOptions: actionTypeOptions,
      rules: { required: true },
      onChangeSelect: value => {
        if (value !== watchActionCode) {
          setValue('eventCode', null, { shouldDirty: true })
        }

        setValue('objectFormCode', null)
        setValue('formObject', null)
        setValue('view', null)
      },
    },
    {
      name: 'eventCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: t('actionInputs.eventCode.label'),
      placeholder: t('placeholder.event'),
      selectOptions: isEmptyActionCode ? [] : eventOptions,
      loading: loadingBindingValuesEvents,
      rules: { required: true },
    },
    ...(watchEvent === EVENT_CODE.ON_DRAG_START && type !== ENTITY_TYPES_REQUEST.TIME_TABLE
      ? [
          {
            name: 'draggableElementText',
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            label: t('actionInputs.draggableElementText.label'),
            autocompleteOptions: draggableElementTextOptions,
            rules: { required: true },
          },
        ]
      : []),
    ...(isShowJSValueField
      ? [
          {
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.INPUT,
            label: t('actionInputs.jsValue.label'),
            labelSx: { minWidth: 120 },
            name: 'jsValue',
            placeholder: t('actionInputs.jsValue.placeholder'),
            readOnly: true,
            additionalBtn: {
              isEnabled: true,
              text: 'edit',
              color: 'primary' as const,
              variant: 'contained' as const,
              onClick: () => {
                setIsOpenScriptValueDialog(true)
                setCurrentNameScriptDialog('jsValue')
              },
            },
            formInputContainerSx: {
              flexWrap: 'nowrap',
              '& .MuiFormControlLabel-root': {
                mb: 1,
              },
              '& .MuiButton-root': {
                ml: 0.5,
              },
            },
            rules: {
              maxLength: MAX_INPUT_LENGTH,
              required: watchActionCode === ACTION_CODE.MODIFY_DATA,
            },
          },
        ]
      : []),
    ...(watchActionCode === ACTION_CODE.SET_VARIABLE_VALUE
      ? [
          {
            name: 'variable',
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            label: t('actionInputs.variable.label'),
            placeholder: t('actionInputs.variable.placeholder'),
            autocompleteOptions: variablesOptions,
            rules: { required: true },
          },
        ]
      : []),
    ...(isContextMenu || isTooltip ? contextMenuOrTooltipInput : []),
  ]

  return {
    actionInputs,
    actionValueInputs,
    actionJSONValueInputs,
    actionViewInputs,
  }
}
